<div class="visit-detail vd">
  <div class="vd__stripe"></div>

  <!-- Loading: a skeleton in the shape of the card, so the dialog does not jump when it fills -->
  <ng-container *ngIf="loading">
    <header class="vd__head">
      <span class="vd__head-icon vd__sk"></span>
      <div class="vd__head-text">
        <span class="vd__sk vd__sk--chip"></span>
        <span class="vd__sk vd__sk--title"></span>
      </div>
      <button type="button" class="vd__close" aria-label="Close" (click)="close()"><mat-icon>close</mat-icon></button>
    </header>
    <div class="vd__body" aria-busy="true" aria-label="Loading visit">
      <div class="vd__sk-row" *ngFor="let w of [72, 54, 64]">
        <span class="vd__sk vd__sk--icon"></span>
        <span class="vd__sk vd__sk--line" [style.width.%]="w"></span>
      </div>
      <span class="vd__sk vd__sk--block"></span>
    </div>
  </ng-container>

  <!-- Could not load -->
  <ng-container *ngIf="!loading && !visit">
    <header class="vd__head">
      <span class="vd__head-icon"><mat-icon fontSet="material-icons-outlined">event_busy</mat-icon></span>
      <div class="vd__head-text"><h2 class="vd__title">Visit</h2></div>
      <button type="button" class="vd__close" aria-label="Close" (click)="close()"><mat-icon>close</mat-icon></button>
    </header>
    <div class="vd__body">
      <div class="vd__banner vd__banner--error" role="alert">
        <mat-icon fontSet="material-icons-outlined">error</mat-icon><span>{{ errorMessage || "The visit could not be loaded." }}</span>
      </div>
    </div>
    <footer class="vd__foot">
      <span class="vd__spacer"></span>
      <button type="button" class="vd__btn vd__btn--secondary" (click)="close()">Close</button>
    </footer>
  </ng-container>

  <ng-container *ngIf="visit">
    <header class="vd__head">
      <span class="vd__head-icon" [class.vd__head-icon--video]="isVideo">
        <mat-icon fontSet="material-icons-outlined">{{ isVideo ? "videocam" : "place" }}</mat-icon>
      </span>
      <div class="vd__head-text">
        <div class="vd__chips">
          <span class="vd__chip" [class.vd__chip--video]="isVideo" [class.vd__chip--person]="!isVideo">{{ isVideo ? "Video visit" : "In-person visit" }}</span>
          <span class="vd__chip" [ngClass]="'vd__chip--' + statusChip.tone"><i class="vd__chip-dot"></i>{{ statusChip.label }}</span>
        </div>
        <h2 class="vd__title" [class.vd__title--cancelled]="isCancelled">{{ visit.title }}</h2>
      </div>
      <button type="button" class="vd__close" aria-label="Close" (click)="close()"><mat-icon>close</mat-icon></button>
    </header>

    <div class="vd__body">
      <!-- When, host, where -->
      <ul class="vd__facts">
        <li class="vd__fact" *ngIf="dayLabel">
          <span class="vd__fact-icon"><mat-icon fontSet="material-icons-outlined">schedule</mat-icon></span>
          <div class="vd__fact-text">
            <b>{{ dayLabel }}</b>
            <span>{{ timeLabel }}<ng-container *ngIf="durationLabel"> · {{ durationLabel }}</ng-container> · {{ zoneLabel }}</span>
          </div>
        </li>
        <li class="vd__fact" *ngIf="visit.hostEmail">
          <span class="vd__fact-icon"><mat-icon fontSet="material-icons-outlined">person</mat-icon></span>
          <div class="vd__fact-text"><b>Host</b><span>{{ visit.hostEmail }}</span></div>
        </li>
        <li class="vd__fact" *ngIf="!isVideo && visit.location">
          <span class="vd__fact-icon"><mat-icon fontSet="material-icons-outlined">place</mat-icon></span>
          <div class="vd__fact-text"><b>Location</b><span>{{ visit.location }}</span></div>
        </li>
      </ul>

      <!-- Video: how to join -->
      <div class="vd__join" *ngIf="isVideo && (visit.meetingCode || visit.joinUrl)">
        <div class="vd__join-top">
          <div class="vd__join-code" *ngIf="visit.meetingCode">
            <span class="vd__mini">Meeting code</span>
            <span class="vd__code">{{ visit.meetingCode }}</span>
          </div>
          <button type="button" class="vd__btn vd__btn--join" *ngIf="visit.joinUrl && !isCancelled" (click)="join()">
            <mat-icon>videocam</mat-icon>Join video visit
          </button>
        </div>
        <div class="vd__link" *ngIf="visit.joinUrl">
          <mat-icon fontSet="material-icons-outlined">link</mat-icon>
          <span class="vd__url" [title]="visit.joinUrl">{{ visit.joinUrl }}</span>
          <button type="button" class="vd__copy" (click)="copyLink()">
            <mat-icon fontSet="material-icons-outlined">{{ copied ? "check" : "content_copy" }}</mat-icon>{{ copied ? "Copied" : "Copy link" }}
          </button>
        </div>
        <p class="vd__join-note" *ngIf="!visit.isHost && !isCancelled">
          <mat-icon fontSet="material-icons-outlined">mail</mat-icon>Use the personal join link from your invitation email.
        </p>
      </div>

      <!-- Notes -->
      <section class="vd__section" *ngIf="visit.notes">
        <h3 class="vd__section-title">Notes</h3>
        <p class="vd__notes">{{ visit.notes }}</p>
      </section>

      <!-- Transcript (video, on request) -->
      <section class="vd__section" *ngIf="isVideo && transcript">
        <h3 class="vd__section-title">Transcript</h3>
        <p class="vd__muted" *ngIf="!transcript.length">No speech has been saved for this visit yet.</p>
        <div class="vd__transcript" *ngIf="transcript.length">
          <p class="vd__line" *ngFor="let line of transcript"><b>{{ line.speakerName }}</b>{{ line.text }}</p>
        </div>
      </section>

      <!-- Attendees -->
      <section class="vd__section">
        <h3 class="vd__section-title">Attendees<b class="vd__count">{{ visit.attendees?.length || 0 }}</b></h3>
        <ul class="vd__people" *ngIf="visit.attendees?.length; else noPeople">
          <li class="vd__person" *ngFor="let a of visit.attendees">
            <span class="vd__avatar" [class.vd__avatar--hcp]="a.attendeeType === 'HCP'">{{ initialsOf(a) }}</span>
            <div class="vd__person-text">
              <span class="vd__person-name">{{ a.displayName || a.email }}</span>
              <span class="vd__person-sub">{{ a.email }}<ng-container *ngIf="a.npi"> · NPI {{ a.npi }}</ng-container></span>
            </div>
            <span class="vd__tag" [class.vd__tag--hcp]="a.attendeeType === 'HCP'">{{ a.attendeeType === "HCP" ? "HCP" : "Internal" }}</span>
            <span class="vd__tag" [class.vd__tag--ok]="a.inviteSentUtc" [class.vd__tag--warn]="!a.inviteSentUtc">
              <mat-icon fontSet="material-icons-outlined">{{ a.inviteSentUtc ? "mark_email_read" : "schedule_send" }}</mat-icon>{{ a.inviteSentUtc ? "Invited" : "Not sent" }}
            </span>
          </li>
        </ul>
        <ng-template #noPeople><p class="vd__muted">No attendees.</p></ng-template>
      </section>

      <div class="vd__banner vd__banner--error" *ngIf="errorMessage" role="alert">
        <mat-icon fontSet="material-icons-outlined">error</mat-icon><span>{{ errorMessage }}</span>
      </div>
      <div class="vd__banner vd__banner--info" *ngIf="infoMessage" role="status">
        <mat-icon fontSet="material-icons-outlined">check_circle</mat-icon><span>{{ infoMessage }}</span>
      </div>
    </div>

    <!-- Cancel confirmation replaces the footer -->
    <footer class="vd__confirm" *ngIf="confirmingCancel; else actions" role="alertdialog" aria-label="Cancel this visit?">
      <mat-icon fontSet="material-icons-outlined">warning</mat-icon>
      <div class="vd__confirm-text">
        <b>Cancel this visit?</b>
        <span>Every attendee gets a cancellation that removes it from their calendar.</span>
      </div>
      <div class="vd__confirm-actions">
        <button type="button" class="vd__btn vd__btn--secondary" (click)="confirmingCancel = false" [disabled]="busy">Keep visit</button>
        <button type="button" class="vd__btn vd__btn--danger" (click)="cancelVisit()" [disabled]="busy">
          {{ busy ? "Cancelling…" : "Cancel visit" }}
        </button>
      </div>
    </footer>

    <ng-template #actions>
      <footer class="vd__foot">
        <button type="button" class="vd__btn vd__btn--danger-text" *ngIf="visit.isHost && !isCancelled" (click)="confirmCancel()" [disabled]="busy">
          <mat-icon fontSet="material-icons-outlined">event_busy</mat-icon>Cancel visit
        </button>
        <span class="vd__spacer"></span>
        <button
          type="button"
          class="vd__btn vd__btn--secondary"
          *ngIf="isVideo && visit.meetingCode"
          (click)="loadTranscript()"
          [disabled]="transcriptLoading"
        >
          <mat-icon fontSet="material-icons-outlined">subtitles</mat-icon>{{ transcriptLoading ? "Loading…" : "Transcript" }}
        </button>
        <ng-container *ngIf="visit.isHost && !isCancelled; else closeOnly">
          <button
            type="button"
            class="vd__btn vd__btn--secondary"
            (click)="resend()"
            [disabled]="busy || isPast"
            [attr.title]="isPast ? 'This visit has ended' : null"
          >
            <mat-icon fontSet="material-icons-outlined">forward_to_inbox</mat-icon>Resend invitations
          </button>
          <button type="button" class="vd__btn vd__btn--primary" (click)="edit()" [disabled]="busy">
            <mat-icon fontSet="material-icons-outlined">edit</mat-icon>Edit
          </button>
        </ng-container>
        <ng-template #closeOnly>
          <button type="button" class="vd__btn vd__btn--secondary" (click)="close()">Close</button>
        </ng-template>
      </footer>
    </ng-template>
  </ng-container>
</div>
